import { Component, computed, effect, forwardRef, input, OnInit, Signal, signal } from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { distinctUntilChanged, filter, map } from 'rxjs'
import { BricksErrorComponent, BricksInputComponent } from '@enbw_bricks/ui'
import { DigitsOnlyDirective } from '../../../directives/digits-only.directive'
import { CustomValidators } from '../../../validators/custom-validators'
import { ALLOWED_COUNTRY } from '../../../../technical-data/technical-resource-data/map/map-config'

type CoordinateType = 'latitude' | 'longitude'

interface GeoRange {
  min: number
  max: number
  country?: string
}

interface FormattedGeoRange {
  min: string
  max: string
  country: string
}

@Component({
  selector: 'app-input-geo',
  imports: [BricksInputComponent, DigitsOnlyDirective, ReactiveFormsModule, BricksErrorComponent],
  templateUrl: './input-geo.component.html',
  styleUrl: './input-geo.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputGeoComponent),
      multi: true,
    },
  ],
})
export class InputGeoComponent implements ControlValueAccessor, OnInit {
  readonly type = input.required<CoordinateType>()
  readonly range = input.required<GeoRange>()
  readonly placeholder = input('')
  readonly isRequired = input(false)
  readonly width = input<'fill' | 'default'>('default')
  readonly isLocationValid = input<Signal<boolean>>(signal(true))
  readonly ctrl = input.required<FormControl<number | null>>()

  protected readonly name = computed(() => this.type().toString())
  protected readonly label = computed(() => (this.type() === 'latitude' ? 'Breitengrad' : 'Längengrad'))
  protected readonly suffix = computed(() => (this.type() === 'latitude' ? '°N' : '°O'))
  protected readonly formattedRange = computed<FormattedGeoRange>(() => ({
    min: InputGeoComponent.formatRangeValue(this.range().min),
    max: InputGeoComponent.formatRangeValue(this.range().max),
    country: this.range().country ?? ALLOWED_COUNTRY,
  }))

  protected readonly error = signal<string>('')
  protected readonly control = computed(() => this.ctrl())

  onChange!: (_: number | null) => void
  onTouched!: () => void

  private static LocationValidator(signal: Signal<boolean>): ValidationErrors | null {
    return signal() ? null : { locationError: true }
  }

  private static RangeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null
    }

    const [, dec] = control.value.toString().split('.')
    return !dec || dec.length < 3 || dec.length > 14 ? { pattern: true } : null
  }

  private static formatRangeValue(value: number): string {
    const [int, dec = '0'] = value.toString().split('.')
    return dec.length < 3 ? `${int}.${dec.padEnd(3, '0')}` : `${int}.${dec.slice(0, 3)}`
  }

  private errorsToErrorMessage(errors: ValidationErrors | null): string {
    if (errors === null) {
      return ''
    }

    switch (true) {
      case errors['zeroIsNotAllowed']:
        return CustomValidators.zeroIsNotAllowedErrorMessage
      case errors['pattern']:
        return CustomValidators.geoCoordinatesPatternErrorMessage
      case Boolean(errors['min'] || errors['max']): {
        const { min, max, country } = this.formattedRange()
        return `Der Wert muss zwischen ${min} und ${max} (${country}) liegen.`
      }
      default:
        return ''
    }
  }

  constructor() {
    effect(() => {
      this.setRequiredState(this.isRequired())
    })

    effect(() => {
      this.isLocationValid()
      this.control().updateValueAndValidity()
    })
  }

  ngOnInit() {
    const ctrl = this.control()
    ctrl.addValidators([
      Validators.min(this.range().min),
      Validators.max(this.range().max),
      InputGeoComponent.RangeValidator,
      CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
      () => InputGeoComponent.LocationValidator(this.isLocationValid()),
    ])

    ctrl.valueChanges
      .pipe(
        distinctUntilChanged(),
        map(() => ctrl.errors),
        filter((errors) => errors !== null || this.error() !== ''),
        map(this.errorsToErrorMessage.bind(this))
      )
      .subscribe((errors) => {
        this.error.set(errors)
      })

    ctrl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      if (this.onChange) {
        this.onChange(value)
      }
    })
  }

  writeValue(value: number): void {
    this.control().setValue(value)
  }

  registerOnChange(fn: (_: number | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    const ctrl = this.control()
    if (isDisabled) {
      ctrl.disable()
    } else if (ctrl.disabled) {
      ctrl.enable()
    }
  }

  handleValueChange(): void {
    if (this.onChange) {
      this.onChange(this.control().value)
    }
  }

  private setRequiredState(isRequired: boolean): void {
    if (isRequired) {
      this.control().addValidators(Validators.required)
    } else if (this.control().hasValidator(Validators.required)) {
      this.control().removeValidators(Validators.required)
    }
  }
}
